import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getPlayerStats, getPlayerRank, ACHIEVEMENTS, exportPlayerData, clearLeaderboard } from '../utils/leaderboardManager';

const { FiHome, FiTrophy, FiClock, FiTarget, FiAward, FiDownload, FiTrash2, FiBarChart3, FiTrendingUp } = FiIcons;

const PlayerStats = () => {
  const navigate = useNavigate();
  const [currentPlayer] = useState(localStorage.getItem('chess_player_name') || 'Player');
  const [playerStats, setPlayerStats] = useState(null);
  const [playerRank, setPlayerRank] = useState(null);

  useEffect(() => {
    const stats = getPlayerStats(currentPlayer);
    const rank = getPlayerRank(currentPlayer);
    setPlayerStats(stats);
    setPlayerRank(rank);
  }, [currentPlayer]);

  const formatTime = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatPlayTime = (milliseconds) => {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getRatingColor = (rating) => {
    if (rating >= 2000) return 'text-purple-400';
    if (rating >= 1600) return 'text-yellow-400';
    if (rating >= 1400) return 'text-blue-400';
    if (rating >= 1200) return 'text-green-400';
    return 'text-gray-400';
  };

  const getRatingTitle = (rating) => {
    if (rating >= 2000) return 'Grandmaster';
    if (rating >= 1800) return 'Master';
    if (rating >= 1600) return 'Expert';
    if (rating >= 1400) return 'Intermediate';
    if (rating >= 1200) return 'Beginner';
    return 'Novice';
  };

  const handleExportData = () => {
    exportPlayerData(currentPlayer);
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all your data? This cannot be undone.')) {
      clearLeaderboard();
      setPlayerStats(getPlayerStats(currentPlayer));
      setPlayerRank(getPlayerRank(currentPlayer));
    }
  };

  if (!playerStats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const playerAchievements = playerStats.achievements.map(id => 
    Object.values(ACHIEVEMENTS).find(achievement => achievement.id === id)
  ).filter(Boolean);

  const availableAchievements = Object.values(ACHIEVEMENTS).filter(achievement => 
    !playerStats.achievements.includes(achievement.id)
  );

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold text-white mb-4 flex items-center justify-center gap-3">
            <SafeIcon icon={FiBarChart3} className="text-blue-400" />
            Player Statistics
          </h1>
          <p className="text-gray-300 text-lg">
            Detailed performance analytics for {currentPlayer}
          </p>
        </motion.div>

        {/* Player Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-600 to-purple-700 p-8 rounded-2xl shadow-2xl border border-white/20 mb-8"
        >
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-white mb-2">{currentPlayer}</h2>
            <div className={`text-2xl font-bold ${getRatingColor(playerStats.rating)}`}>
              {playerStats.rating} • {getRatingTitle(playerStats.rating)}
            </div>
            {playerRank && (
              <div className="text-white/80 text-lg mt-2">
                Global Rank: #{playerRank}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{playerStats.gamesPlayed}</div>
              <div className="text-white/80">Games Played</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">{playerStats.wins}</div>
              <div className="text-white/80">Wins</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400">{playerStats.winRate.toFixed(1)}%</div>
              <div className="text-white/80">Win Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-400">{playerStats.longestWinStreak}</div>
              <div className="text-white/80">Best Streak</div>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Game Statistics */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <SafeIcon icon={FiTrophy} className="text-yellow-400" />
              Game Statistics
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-400">{playerStats.wins}</div>
                  <div className="text-gray-300 text-sm">Wins</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-400">{playerStats.losses}</div>
                  <div className="text-gray-300 text-sm">Losses</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-400">{playerStats.draws}</div>
                  <div className="text-gray-300 text-sm">Draws</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-500">{playerStats.timeouts}</div>
                  <div className="text-gray-300 text-sm">Timeouts</div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Current Win Streak:</span>
                    <span className="text-orange-400 font-semibold">{playerStats.currentWinStreak}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Longest Win Streak:</span>
                    <span className="text-orange-400 font-semibold">{playerStats.longestWinStreak}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Win Rate:</span>
                    <span className="text-green-400 font-semibold">{playerStats.winRate.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Performance Metrics */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="text-blue-400" />
              Performance Metrics
            </h3>
            
            <div className="space-y-4">
              <div className="bg-white/5 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Average Game Time:</span>
                  <span className="text-white font-semibold">
                    {formatTime(playerStats.avgGameTime)}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full" 
                    style={{ width: `${Math.min((playerStats.avgGameTime / 120000) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Total Play Time:</span>
                  <span className="text-white font-semibold">
                    {formatPlayTime(playerStats.totalPlayTime)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Fastest Win:</span>
                  <span className="text-green-400 font-semibold">
                    {playerStats.fastestWin ? formatTime(playerStats.fastestWin) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Average Moves per Game:</span>
                  <span className="text-white font-semibold">
                    {playerStats.avgMovesPerGame.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Checks Delivered:</span>
                  <span className="text-yellow-400 font-semibold">{playerStats.checksDelivered}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Checkmates Delivered:</span>
                  <span className="text-red-400 font-semibold">{playerStats.checkmatesDelivered}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Pieces Captured:</span>
                  <span className="text-purple-400 font-semibold">{playerStats.piecesCaptures}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 mb-8"
        >
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <SafeIcon icon={FiAward} className="text-purple-400" />
            Achievements ({playerAchievements.length}/{Object.keys(ACHIEVEMENTS).length})
          </h3>

          {/* Earned Achievements */}
          {playerAchievements.length > 0 && (
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-green-400 mb-4">Earned Achievements</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {playerAchievements.map((achievement) => (
                  <motion.div
                    key={achievement.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg"
                  >
                    <span className="text-3xl">{achievement.icon}</span>
                    <div>
                      <div className="text-white font-semibold">{achievement.name}</div>
                      <div className="text-gray-300 text-sm">{achievement.description}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Available Achievements */}
          {availableAchievements.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-400 mb-4">Available Achievements</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableAchievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-lg opacity-60"
                  >
                    <span className="text-3xl grayscale">{achievement.icon}</span>
                    <div>
                      <div className="text-gray-300 font-semibold">{achievement.name}</div>
                      <div className="text-gray-400 text-sm">{achievement.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center gap-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiHome} />
            Back to Menu
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/leaderboard')}
            className="flex items-center gap-2 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiTrophy} />
            View Leaderboard
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExportData}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiDownload} />
            Export Data
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClearData}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiTrash2} />
            Clear Data
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default PlayerStats;